import { useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Button, Alert, MenuItem } from '@mui/material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { templesService } from '../../services/temples.service';

const scheduleSchema = z.object({
  name: z.string().min(1, 'Укажите название'),
  location: z.string().min(1, 'Укажите населённый пункт'),
  url: z.string().url('Некорректный URL').optional().or(z.literal('')),
  templeId: z.string().optional(), // Select value as string; '' = none
});

type ScheduleFormValues = z.infer<typeof scheduleSchema>;

interface Props {
  open: boolean; onClose: () => void;
  onSubmit: (data: { name: string; location: string; url?: string; templeId?: number | null }) => Promise<void>;
  initialData?: any | null; loading: boolean; error?: string | null;
}

export function SchedulesForm({ open, onClose, onSubmit, initialData, loading, error }: Props) {
  const { register, handleSubmit, formState: { errors }, reset } = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: { name: '', location: '', url: '', templeId: '' },
  });

  const { data: templesData } = useQuery({
    queryKey: ['temples', 'for-schedule-select'],
    queryFn: () => templesService.getList({ limit: 100 }),
    enabled: open,
  });
  const temples = templesData?.data || [];

  useEffect(() => {
    if (initialData) {
      reset({
        name: initialData.name || '',
        location: initialData.location || '',
        url: initialData.url || '',
        templeId: initialData.temple_id ? String(initialData.temple_id) : '',
      });
    } else {
      reset({ name: '', location: '', url: '', templeId: '' });
    }
  }, [initialData, reset, open]);

  const submit = async (data: ScheduleFormValues) => {
    await onSubmit({
      name: data.name,
      location: data.location,
      url: data.url || undefined,
      templeId: data.templeId ? parseInt(data.templeId) : null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? 'Редактировать расписание' : 'Добавить расписание'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Название" fullWidth {...register('name')} error={!!errors.name} helperText={errors.name?.message} />
          <TextField label="Населённый пункт" fullWidth {...register('location')} error={!!errors.location} helperText={errors.location?.message} />
          <TextField label="Ссылка на расписание (URL)" fullWidth {...register('url')} error={!!errors.url} helperText={errors.url?.message} />
          <TextField label="Храм (необязательно)" select fullWidth defaultValue="" {...register('templeId')}>
            <MenuItem value="">— не выбран —</MenuItem>
            {temples.map((t) => (<MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button onClick={handleSubmit(submit)} variant="contained" disabled={loading}>{loading ? 'Сохранение...' : 'Сохранить'}</Button>
      </DialogActions>
    </Dialog>
  );
}
